import React, { useState, useMemo, useRef, useEffect } from "react";

export type Option = {
  name?: string;
  label?: string;
  value: string | number;
  disabled?: boolean;
};

export type DropdownProps = {
  name: string;
  options: Option[];
  value: string | number | (string | number)[];
  onChange: (e: { target: { name: string; value: any } }) => void;
  className?: string;
  searchable?: boolean;
  multiple?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showSelectAll?: boolean;
};

const Dropdown: React.FC<DropdownProps> = ({
  name,
  options = [],
  value,
  onChange,
  className = "",
  searchable = false,
  multiple = false,
  placeholder = "Select...",
  disabled = false,
  showSelectAll = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
      setSearchTerm("");
    }
  }, [disabled]);

  const filteredOptions = useMemo(() => {
    return options.filter((opt) => {
      const optionName =
        opt.name !== undefined
          ? String(opt.name)
          : opt.label !== undefined
          ? String(opt.label)
          : "";
      return optionName.toLowerCase().includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, options]);

  const isSelected = (val: string | number): boolean => {
    const strVal = String(val);
    return multiple && Array.isArray(value)
      ? value.map(String).includes(strVal)
      : String(value) === strVal;
  };

  const handleOptionClick = (val: string | number) => {
    const strVal = String(val);
    if (multiple && Array.isArray(value)) {
      const currentValues = value.map(String);
      const newValue = currentValues.includes(strVal)
        ? currentValues.filter((v) => v !== strVal)
        : [...currentValues, strVal];
      onChange({ target: { name, value: newValue } });
    } else {
      onChange({ target: { name, value: strVal } });
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  const nonDisabledFiltered = useMemo(
    () => filteredOptions.filter((opt) => !opt.disabled),
    [filteredOptions]
  );

  const allFilteredSelected =
    nonDisabledFiltered.length > 0 &&
    nonDisabledFiltered.every((opt) => isSelected(opt.value));

  const handleSelectAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!multiple || !Array.isArray(value)) return;

    const filteredValues = nonDisabledFiltered.map((opt) => String(opt.value));
    const currentValues = value.map(String);

    let newValue: string[];
    if (allFilteredSelected) {
      newValue = currentValues.filter((val) => !filteredValues.includes(val));
    } else {
      newValue = Array.from(new Set([...currentValues, ...filteredValues]));
    }
    onChange({ target: { name, value: newValue } });
  };

  const displayText = () => {
    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      const selectedLabels = options
        .filter((opt) => value.map(String).includes(String(opt.value)))
        .map((opt) => opt.name ?? opt.label ?? String(opt.value));

      if (selectedLabels.length === 0) return placeholder;
      if (selectedLabels.length === options.length && options.length > 1) {
        return `All Selected (${selectedLabels.length})`;
      }
      if (selectedLabels.length <= 2) {
        return selectedLabels.join(", ");
      }
      return `${selectedLabels.slice(0, 2).join(", ")} (+${
        selectedLabels.length - 2
      } more)`;
    } else {
      const selected = options.find(
        (opt) => String(opt.value) === String(value)
      );
      return selected
        ? selected.name ?? selected.label ?? String(selected.value)
        : placeholder;
    }
  };

  const hasBorderColor = /border-(red|green|blue|gray|teal|yellow)-\d+/.test(
    className
  );
  const borderClass = hasBorderColor
    ? ""
    : disabled
    ? "border-gray-200"
    : "border-gray-300";

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`border rounded px-3 py-2 bg-white ${
          disabled
            ? "cursor-not-allowed text-gray-400 bg-gray-100"
            : "cursor-pointer"
        } ${borderClass} ${className}`}
        onClick={() => {
          if (disabled) return;
          setIsOpen((prev) => !prev);
        }}
        aria-disabled={disabled}
      >
        <span className="flex justify-between items-center gap-2">
          <span className="flex-1 truncate text-left">{displayText()}</span>
          <svg
            className={`w-4 h-4 flex-shrink-0 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded max-h-60 overflow-auto shadow-lg">
          {searchable && (
            <div className="p-2 border-b sticky top-0 bg-white z-10">
              <input
                type="text"
                placeholder="Search..."
                className="w-full px-2 py-1 text-sm border rounded outline-none focus:border-teal-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {multiple && showSelectAll && nonDisabledFiltered.length > 0 && (
            <div
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider cursor-pointer border-b hover:bg-gray-100 flex items-center justify-between select-none ${
                allFilteredSelected
                  ? "text-teal-700 bg-teal-50"
                  : "text-gray-600 bg-gray-50"
              }`}
              onClick={handleSelectAll}
            >
              <span>{allFilteredSelected ? "Deselect All" : "Select All"}</span>
              <span className="text-[11px] font-normal lowercase text-gray-500">
                ({Array.isArray(value) ? value.length : 0} selected)
              </span>
            </div>
          )}
          {filteredOptions.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-500 text-center">
              No options available
            </div>
          ) : (
            filteredOptions.map((opt) => {
              const selected = isSelected(opt.value);
              const optLabel = opt.name ?? opt.label ?? String(opt.value);
              return (
                <div
                  key={String(opt.value)}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm flex items-center select-none ${
                    selected
                      ? "bg-teal-50 font-medium text-teal-900"
                      : "text-gray-700"
                  } ${opt.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => !opt.disabled && handleOptionClick(opt.value)}
                >
                  {multiple && (
                    <input
                      type="checkbox"
                      checked={selected}
                      readOnly
                      disabled={opt.disabled}
                      className="w-4 h-4 text-teal-600 rounded mr-2.5 pointer-events-none accent-teal-600"
                    />
                  )}
                  <span className="flex-1 truncate">{optLabel}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default Dropdown;

